import { BellIcon, ChevronDownIcon, HelpCircleIcon, MoonIcon, SunIcon } from 'lucide-react';
import type { UiLinkComponent } from '../lib/link.js';
import { cn } from '../lib/utils.js';

export interface BreadcrumbItem {
  label: string;
  href?: string;
}

export interface AdminTopbarProps {
  breadcrumbs: BreadcrumbItem[];
  searchPlaceholder?: string;
  periodLabel?: string;
  userInitials: string;
  userName: string;
  userRole: string;
  notificationCount?: number;
  isDark?: boolean;
  onToggleTheme?: () => void;
  className?: string;
  /** Component used to render breadcrumb links; defaults to a plain `<a>` */
  linkComponent?: UiLinkComponent;
}

export function AdminTopbar({
  breadcrumbs,
  userInitials,
  userName,
  userRole,
  notificationCount = 0,
  isDark = false,
  onToggleTheme,
  className,
  linkComponent,
}: AdminTopbarProps) {
  const Comp = linkComponent ?? 'a';
  return (
    <div className={cn('flex w-full items-center gap-4 px-6', className)}>
      {/* Breadcrumbs */}
      <nav className="flex items-center gap-1.5 text-[13px] text-muted-foreground">
        {breadcrumbs.map((crumb, i) => (
          <span key={crumb.label} className="flex items-center gap-1.5">
            {i > 0 && <span className="text-muted-foreground/40">/</span>}
            {i === breadcrumbs.length - 1 ? (
              <span className="font-semibold text-foreground">{crumb.label}</span>
            ) : (
              <Comp href={crumb.href ?? '#'} className="hover:text-foreground transition-colors">
                {crumb.label}
              </Comp>
            )}
          </span>
        ))}
      </nav>

      {/* Actions */}
      <div className="ml-auto flex items-center gap-2">
        {onToggleTheme && (
          <IconButton onClick={onToggleTheme} title="Toggle theme">
            {isDark ? (
              <SunIcon className="h-[17px] w-[17px]" />
            ) : (
              <MoonIcon className="h-[17px] w-[17px]" />
            )}
          </IconButton>
        )}
        <IconButton title="Help">
          <HelpCircleIcon className="h-[17px] w-[17px]" />
        </IconButton>
        <IconButton title="Notifications" hasDot={notificationCount > 0}>
          <BellIcon className="h-[17px] w-[17px]" />
        </IconButton>

        <div className="mx-1 h-6 w-px bg-border" />

        {/* User chip */}
        <button
          type="button"
          className="flex items-center gap-2.5 rounded-full border border-border bg-card py-1 pl-1 pr-2.5 transition-colors hover:bg-muted/60"
        >
          <div
            className="flex h-7 w-7 items-center justify-center rounded-full text-[11px] font-bold"
            style={{
              // Was `var(--gold, #FFC832)` / `var(--ink, #121A37)` — neither token exists in
              // the contract, so every brand rendered the same hardcoded gold fallback.
              background:
                'linear-gradient(135deg, var(--color-secondary) 0%, color-mix(in oklab, var(--color-secondary) 82%, var(--color-foreground)) 100%)',
              color: 'var(--color-secondary-foreground)',
            }}
          >
            {userInitials}
          </div>
          <div className="text-left">
            <div className="text-[12.5px] font-semibold leading-tight text-foreground">
              {userName}
            </div>
            <div className="text-[10.5px] text-muted-foreground">{userRole}</div>
          </div>
          <ChevronDownIcon className="ml-0.5 h-3.5 w-3.5 text-muted-foreground" />
        </button>
      </div>
    </div>
  );
}

function IconButton({
  children,
  hasDot,
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement> & { hasDot?: boolean }) {
  return (
    <button
      type="button"
      {...props}
      className="relative flex h-9 w-9 items-center justify-center rounded-[10px] text-muted-foreground transition-colors hover:bg-muted/60 hover:text-foreground"
    >
      {children}
      {hasDot && (
        <span
          className="absolute right-2 top-2 h-[7px] w-[7px] rounded-full border-2 border-card"
          style={{ background: 'var(--color-secondary)' }}
        />
      )}
    </button>
  );
}
